import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import iziToast from 'izitoast';

import {
  assignTeacher, closeTeacher, getTeacherCandidates, updateTeacher,
} from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { formatDate, todayString } from '../../utils/journalFormat';

// Та же палитра, что в выборе своего цвета: журнал читают быстро, соседние оттенки в нём
// бесполезны. Цвет нужен, чтобы в листе было видно, кто выдал часы.
const PRESETS = [
  '#1E88E5', '#00897B', '#43A047', '#FDD835',
  '#FB8C00', '#E53935', '#8E24AA', '#3949AB',
  '#D81B60', '#6D4C41', '#546E7A', '#F4511E',
];

const ColorPicker = ({ value, onChange }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
      Цвет часов в журнале
    </Typography>
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {PRESETS.map((color) => (
        <Box
          key={color}
          onClick={() => onChange(color)}
          sx={{
            width: 24, height: 24, borderRadius: '4px', cursor: 'pointer',
            background: color,
            outline: value === color ? '2px solid #2f2f2f' : '1px solid #e0e0e0',
            outlineOffset: value === color ? 1 : 0,
          }}
        />
      ))}
      <Tooltip title="Без цвета">
        <Box
          onClick={() => onChange('')}
          sx={{
            width: 24, height: 24, borderRadius: '4px', cursor: 'pointer',
            background: 'repeating-linear-gradient(45deg, #fff, #fff 4px, #eee 4px, #eee 8px)',
            outline: value ? '1px solid #e0e0e0' : '2px solid #2f2f2f',
          }}
        />
      </Tooltip>
    </Stack>
  </Box>
);

/**
 * Назначение и правка преподавателя группы.
 *
 * Назначает администратор или редактор, не сам преподаватель: по решению заказчика
 * (вопрос О-5) самоназначение означало бы доступ к любой группе. До этого состав вёлся
 * скриптом через ssh.
 *
 * «Снять с группы» не удаляет запись, а закрывает её датой: выработка часов за прошлые
 * месяцы считается по связям, и удаление переписало бы историю.
 *
 * @param state  { mode: 'create' } | { mode: 'edit', teacher } — строка из карточки группы
 */
const TeacherModal = ({ state, groupId, onClose, onSaved }) => {
  const open = Boolean(state);
  const isEdit = state?.mode === 'edit';
  const teacher = state?.teacher;

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(isEdit ? {
      user: null,
      is_curator: Boolean(teacher.is_curator),
      is_hourly: Boolean(teacher.is_hourly),
      color: teacher.color || '',
      date_from: teacher.date_from || '',
      date_to: teacher.date_to || '',
    } : {
      user: null,
      is_curator: false,
      is_hourly: false,
      color: '',
      date_from: todayString(),
      date_to: '',
    });

    if (isEdit) return;

    setLoading(true);
    getTeacherCandidates(groupId)
      .then(setCandidates)
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, teacher, groupId]);

  if (!open || !form) return null;

  const change = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));
  const toggle = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.checked }));

  const run = (request, message) => {
    setSaving(true);
    request
      .then(() => {
        iziToast.success({ message, position: 'topRight' });
        onSaved();
      })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  const submit = () => {
    const payload = {
      is_curator: form.is_curator,
      is_hourly: form.is_hourly,
      color: form.color || null,
      date_from: form.date_from,
    };

    if (isEdit) {
      run(
        updateTeacher(groupId, teacher.id, { ...payload, date_to: form.date_to || null }),
        'Данные преподавателя изменены',
      );
      return;
    }

    run(
      assignTeacher(groupId, { ...payload, user_id: form.user.id }),
      `${form.user.label} назначен в группу`,
    );
  };

  const remove = () => run(
    closeTeacher(groupId, teacher.id, todayString()),
    'Преподаватель снят с группы',
  );

  const options = candidates.map((item) => ({
    id: item.id,
    label: item.name,
    role: item.role,
    email: item.email,
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {isEdit ? teacher.name : 'Назначить преподавателя'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && (
            <Autocomplete
              options={options}
              loading={loading}
              value={form.user}
              onChange={(event, value) => setForm((prev) => ({ ...prev, user: value }))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="Свободных преподавателей нет"
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span>
                    {option.label}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.role} · {option.email}
                    </Typography>
                  </span>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Преподаватель" helperText="Отметки ставят «Преподаватель ЭЖ», «Редактор», «Администратор»" />
              )}
            />
          )}

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Switch checked={form.is_curator} onChange={toggle('is_curator')} />}
              label="Куратор"
            />
            <FormControlLabel
              control={<Switch checked={form.is_hourly} onChange={toggle('is_hourly')} />}
              label="Почасовик"
            />
          </Stack>

          {form.is_curator && form.is_hourly && (
            <Typography variant="caption" color="error">
              Почасовик не может быть куратором группы
            </Typography>
          )}

          <ColorPicker
            value={form.color}
            onChange={(color) => setForm((prev) => ({ ...prev, color }))}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Ведёт с" type="date" value={form.date_from}
              onChange={change('date_from')} InputLabelProps={{ shrink: true }} fullWidth
            />
            {isEdit && (
              <TextField
                label="По" type="date" value={form.date_to}
                onChange={change('date_to')} InputLabelProps={{ shrink: true }} fullWidth
                helperText="Пусто — ведёт сейчас"
              />
            )}
          </Stack>

          {isEdit && teacher.date_to && (
            <Typography variant="caption" color="text.secondary">
              Связь закрыта {formatDate(teacher.date_to)}. Чтобы вернуть преподавателя
              в группу, очистите поле «По».
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {isEdit && !teacher.date_to && (
          <Button
            size="small" disabled={saving} onClick={remove}
            sx={{ color: '#b0504a' }}
          >
            Снять с группы
          </Button>
        )}
        <Button onClick={onClose} sx={{ ml: 'auto' }}>Отмена</Button>
        <Button
          variant="contained" onClick={submit}
          disabled={saving || (!isEdit && !form.user) || (form.is_curator && form.is_hourly)}
        >
          {isEdit ? 'Сохранить' : 'Назначить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeacherModal;
